/**
 * mega-menu.js - El Enemigo
 * Custom Element: <io-header-mega-menu>
 */

class IOHeaderMegaMenu extends HTMLElement {
  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('.mega-menu__trigger'));
    this.overlay = document.getElementById('io-mega-overlay');
    this._active = null;
    this._closeT = null;
    this._pointer = null;
    // 'mouse' by default; updated to 'touch'/'pen' on pointerdown, reset on mousemove
    this._lastPointerType = 'mouse';

    this._handlePointerDown = (event) => {
      this._lastPointerType = event.pointerType || 'mouse';
      // Bug C fix: on touch/pen, close if tapping outside the mega-menu.
      // iOS does not fire 'click' on non-interactive elements, so document.click alone is not enough.
      if ((event.pointerType === 'touch' || event.pointerType === 'pen') && this._active && !this.contains(event.target)) {
        this._closeAll(false);
      }
    };

    this._handlePointerMove = (event) => {
      this._pointer = { x: event.clientX, y: event.clientY };
      // Do NOT reset _lastPointerType here.
      // iOS Safari fires a synthetic 'mousemove' before the 'click' event in the touch
      // sequence, which would override the 'touch' type and break touch detection.
    };

    // Bug A fix: use pointerenter/pointerleave instead of mouseenter/mouseleave.
    // These events carry event.pointerType so we can filter touch/pen reliably
    // without relying on _lastPointerType state (which can be corrupted by synthetic events).
    this._handleRootEnter = (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(this._closeT);
    };

    this._handleRootLeave = (event) => {
      if (!this._active) return;
      if (event.pointerType !== 'mouse') return;
      this._scheduleClose(this._active.trigger, this._active.panel, event, 420);
    };

    this._handleFocusOut = () => {
      requestAnimationFrame(() => {
        if (!this._active) return;
        if (this.contains(document.activeElement)) return;
        this._closeAll(false);
      });
    };

    this.triggers.forEach((trigger) => this._bindTrigger(trigger));

    this.addEventListener('pointerenter', this._handleRootEnter);
    this.addEventListener('pointerleave', this._handleRootLeave);
    this.addEventListener('focusout', this._handleFocusOut);
    document.addEventListener('pointerdown', this._handlePointerDown, { passive: true });
    document.addEventListener('mousemove', this._handlePointerMove, { passive: true });

    // Bug B fix: store handlers as instance properties so they can be removed in disconnectedCallback.
    this._handleKeydown = (event) => {
      if (event.key === 'Escape') this._closeAll(true);
    };
    this._handleDocClick = (event) => {
      if (this._active && !this.contains(event.target)) this._closeAll(false);
    };
    document.addEventListener('keydown', this._handleKeydown);
    document.addEventListener('click', this._handleDocClick);

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this._closeAll(false));
    }

    this._mobileDrawer = new IOHeaderMobileDrawer(this);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerenter', this._handleRootEnter);
    this.removeEventListener('pointerleave', this._handleRootLeave);
    this.removeEventListener('focusout', this._handleFocusOut);
    document.removeEventListener('pointerdown', this._handlePointerDown);
    document.removeEventListener('mousemove', this._handlePointerMove);
    document.removeEventListener('keydown', this._handleKeydown);
    document.removeEventListener('click', this._handleDocClick);
  }

  _bindTrigger(trigger) {
    const btn = trigger.querySelector('.mega-menu__trigger-btn');
    const panel = trigger.querySelector('.mega-menu__panel');
    if (!btn || !panel) return;

    if (!panel.id) panel.id = `mm-${Math.random().toString(36).slice(2, 7)}`;
    btn.setAttribute('aria-controls', panel.id);
    btn.setAttribute('aria-expanded', 'false');

    trigger.addEventListener('pointerenter', (event) => {
      // Only open on real mouse hover; touch/pen interactions use the click handler
      if (event.pointerType !== 'mouse') return;
      clearTimeout(this._closeT);
      this._open(trigger, btn, panel);
    });

    trigger.addEventListener('focusin', () => this._open(trigger, btn, panel));
    panel.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(this._closeT);
    });

    btn.addEventListener('click', (event) => {
      const isExpanded = btn.getAttribute('aria-expanded') === 'true';
      const hasRealHref = btn.tagName === 'A' && btn.getAttribute('href') && btn.getAttribute('href') !== '#';
      const isTouch = this._lastPointerType === 'touch' || this._lastPointerType === 'pen';

      if (isTouch) {
        if (isExpanded && hasRealHref) {
          // Bug D fix: second tap on an open trigger with a real href → navigate.
          // This lets touch users reach the top-level category page.
          return;
        }
        event.preventDefault();
        if (isExpanded) {
          this._closeAll(false);
        } else {
          // First tap → open panel
          this._open(trigger, btn, panel);
        }
        return;
      }

      // Mouse behavior: if the trigger has a real href, let the click navigate.
      // Hover already opens the panel for desktop users.
      if (hasRealHref) return;

      event.preventDefault();
      if (isExpanded) {
        this._closeAll(true);
      } else {
        this._open(trigger, btn, panel);
      }
    });

    btn.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this._open(trigger, btn, panel);
        const first = panel.querySelector('a, button');
        if (first) first.focus();
      }
    });

    panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const items = Array.from(panel.querySelectorAll('a[href], button'));
      if (!items.length) return;

      if (!event.shiftKey && document.activeElement === items[items.length - 1]) {
        this._closeAll(false);
      }

      if (event.shiftKey && document.activeElement === items[0]) {
        event.preventDefault();
        btn.focus();
        this._closeAll(false);
      }
    });
  }

  _open(trigger, btn, panel) {
    clearTimeout(this._closeT);

    if (this._active && this._active.panel !== panel) {
      this._close(this._active);
    }

    trigger.classList.add('is-active');
    btn.setAttribute('aria-expanded', 'true');
    panel.classList.add('is-open');

    if (this.overlay) this.overlay.classList.add('is-visible');

    this._active = { trigger, btn, panel };
  }

  _close(item) {
    item.trigger.classList.remove('is-active');
    item.btn.setAttribute('aria-expanded', 'false');
    item.panel.classList.remove('is-open');
  }

  _scheduleClose(trigger, panel, event, delay) {
    const nextTarget = event.relatedTarget;
    if (nextTarget && (trigger.contains(nextTarget) || panel.contains(nextTarget))) return;

    clearTimeout(this._closeT);
    this._closeT = setTimeout(() => {
      if (trigger.matches(':hover') || panel.matches(':hover')) return;

      const pointerX = this._pointer?.x ?? event.clientX;
      const pointerY = this._pointer?.y ?? event.clientY;
      const hoveredElement =
        Number.isFinite(pointerX) && Number.isFinite(pointerY)
          ? document.elementFromPoint(pointerX, pointerY)
          : null;

      if (hoveredElement && (trigger.contains(hoveredElement) || panel.contains(hoveredElement))) return;

      this._closeAll(false);
    }, delay);
  }

  _closeAll(returnFocus) {
    clearTimeout(this._closeT);

    if (this._active) {
      this._close(this._active);
      if (returnFocus) this._active.btn.focus();
      this._active = null;
    }

    if (this.overlay) this.overlay.classList.remove('is-visible');
  }
}

class IOHeaderMobileDrawer {
  constructor(root) {
    this.root = root;
    this.drawer = root.querySelector('[data-mobile-drawer]');
    this.backdrop = root.querySelector('[data-mobile-backdrop]');
    if (!this.drawer) return;

    const openBtn = root.querySelector('[data-mobile-open]');
    const closeBtn = root.querySelector('[data-mobile-close]');

    if (openBtn) openBtn.addEventListener('click', () => this.open());
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());
    if (this.backdrop) this.backdrop.addEventListener('click', () => this.close());

    this.drawer.querySelectorAll('[data-mobile-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const item = btn.closest('[data-mobile-accordion]');
        if (!item) return;

        const wasOpen = item.classList.contains('is-open');
        item.parentElement.querySelectorAll('.is-open').forEach((openItem) => openItem.classList.remove('is-open'));
        if (!wasOpen) item.classList.add('is-open');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) this.close();
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    if (this.backdrop) this.backdrop.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.drawer.classList.remove('is-open');
    if (this.backdrop) this.backdrop.classList.remove('is-open');
    document.body.style.overflow = '';
  }
}

customElements.define('io-header-mega-menu', IOHeaderMegaMenu);

function syncDesktopFacetsOffset() {
  const headerSection = document.querySelector('.section-io-header');
  if (!headerSection) return;

  const root = document.documentElement;
  let frame = null;

  const update = () => {
    frame = null;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const headerBottom = Math.min(
      viewportHeight,
      Math.max(0, Math.round(headerSection.getBoundingClientRect().bottom))
    );
    root.style.setProperty('--io-header-viewport-bottom', `${headerBottom}px`);
  };

  const requestUpdate = () => {
    if (frame !== null) return;
    frame = window.requestAnimationFrame(update);
  };

  update();
  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  document.addEventListener('shopify:section:load', requestUpdate);
  document.addEventListener('shopify:section:reorder', requestUpdate);

  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(requestUpdate);
    observer.observe(headerSection);

    const marqueeSection = document.querySelector('.section-io-marquee');
    if (marqueeSection) observer.observe(marqueeSection);
  }
}

syncDesktopFacetsOffset();
